import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://sakeenahsitters.com";
const NAME = "Sakeenah Circle";
const DEFAULT_DESC = "A premium membership platform connecting families with verified, CPR-certified caregivers. Search by postal code, language and availability — book with confidence.";
const DEFAULT_OG = `${SITE}/og-cover.jpg`;

export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} · ${NAME}` : `${NAME} — Trusted, verified caregivers near you`;
  const canonical = `${SITE}${path.startsWith("/") ? path : "/" + path}`.replace(/\/$/, "") || SITE;
  const structured = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <meta name="theme-color" content="#FAF7F2" />

      {structured.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

// ---------- Common JSON-LD builders ----------
export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: NAME,
  url: SITE,
  logo: `${SITE}/logo.png`,
  description: DEFAULT_DESC,
  sameAs: [],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: NAME,
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/browse?postal_code={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question", name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const localBusinessSchema = ({ city, path, count, rating_avg }) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: `${NAME} — ${city}`,
  url: `${SITE}${path}`,
  image: DEFAULT_OG,
  description: `Verified, CPR-certified caregivers in ${city}. Search by language, availability and rating.`,
  address: { "@type": "PostalAddress", addressLocality: city, addressCountry: "CA" },
  priceRange: "$$",
  ...(rating_avg > 0 && count > 0
    ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: rating_avg,
          reviewCount: count,
          bestRating: 5,
          worstRating: 1,
        },
      }
    : {}),
});

export const sitterProfileSchema = (s, reviews = []) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: s.name,
    description: s.bio || s.headline,
    jobTitle: "Verified Caregiver",
    address: { "@type": "PostalAddress", addressLocality: s.city || "", postalCode: s.postal_code || "", addressCountry: "CA" },
    knowsLanguage: s.languages || [],
    url: `${SITE}/babysitters/${s.slug || s.id}`,
  };
  if (s.rating_avg > 0 && s.rating_count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: s.rating_avg,
      reviewCount: s.rating_count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (reviews && reviews.length) {
    schema.review = reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.parent_name || "Verified family" },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      reviewBody: r.text,
      datePublished: r.created_at,
    }));
  }
  return schema;
};

export const serviceOfferSchema = (s) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Childcare",
  provider: { "@type": "Person", name: s.name },
  areaServed: s.city || "",
  offers: s.hourly_rate ? {
    "@type": "Offer",
    price: s.hourly_rate,
    priceCurrency: "CAD",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: s.hourly_rate,
      priceCurrency: "CAD",
      unitText: "HOUR",
    },
  } : undefined,
});
